import ModalViewModel from '../../../../infra/ModalViewModel'
import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import Workspace from '../../../../iam/models/Workspace'
import WorkspaceSelectorViewModel from '../../WorkspaceSelectorForBundlesViewModel'
import { AlertFactory, AlertState } from '../../../../alerts/Alert'
import { Bundle } from 'admin/bundle/bundle'
import { IWithWorkspaceSelectionForBundles } from '../../components/BundleFormWorkpaceDropdown'
import { validationContext } from 'validx'
import { CreateBundleParams } from 'admin/bundle/dtos/dtos'
import { Id } from 'types/definitions'
import { mapBundleIncludedItemToIds } from '../../dtos/bundleGrpcDtoMapper'
import { BundleStore } from '../../BundleStore'
import { RefreshBundles } from '../../BundleSearchViewModel'

export class BulkDuplicateBundleViewModel
  extends ModalViewModel
  implements IWithWorkspaceSelectionForBundles
{
  /**
   * The selected workspace
   */
  @observable
  workspace?: Workspace = undefined

  @observable
  selectedBundles: Bundle[] = []

  @observable
  incompatibleBundleItemIdsMap = new Map<Id, Set<Id>>()

  @observable
  incompatibleBundleIds = new Set<Id>()

  @observable
  compatibleBundles: CreateBundleParams[] = []

  workspaceValidation = validationContext(this, {
    workspace: [(opts) => !!opts.value || 'Workspace is required'],
  })

  constructor(
    public workspaceSelectorVM: WorkspaceSelectorViewModel,
    private newAlert: AlertFactory,
    private bundleStore: BundleStore,
    private refreshBundles: RefreshBundles,
  ) {
    super()
    makeObservable(this)
    this.close$.subscribe(this.reset)
  }

  @computed
  get incompatibleBundleList() {
    return this.selectedBundles.filter((bundle) =>
      this.incompatibleBundleIds.has(bundle.Id),
    )
  }

  @computed
  get partiallyCompatibleBundleList() {
    return this.selectedBundles.filter((bundle) =>
      this.incompatibleBundleItemIdsMap.has(bundle.Id),
    )
  }

  @action.bound
  setWorkspace(workspace?: Workspace) {
    this.workspace = workspace
    this.compatibleBundles = []
    this.incompatibleBundleItemIdsMap.clear()

    if (!workspace) {
      return
    }

    for (const bundle of this.selectedBundles) {
      const incompatibleBundle =
        bundle.baseItem.workspaceId &&
        bundle.baseItem.workspaceId !== workspace.id

      // we can't duplicate these bundles
      if (incompatibleBundle) {
        this.incompatibleBundleIds.add(bundle.Id)
        continue
      }

      // we can duplicate these bundles but without these service items
      const incompatibleItems = new Set(
        bundle.includedItems
          .filter(
            (item) => item.workspaceId && item.workspaceId !== workspace.id,
          )
          .map((item) => item.inventoryItemId),
      )

      if (incompatibleItems.size) {
        this.incompatibleBundleItemIdsMap.set(bundle.Id, incompatibleItems)
      }

      const includedItemsIds = bundle.includedItems.filter(
        (item) =>
          !item.isBaseItem && !incompatibleItems.has(item.inventoryItemId),
      )

      this.compatibleBundles.push({
        name: bundle.name,
        description: bundle.description,
        baseItemId: Id.assert(bundle.baseItem.inventoryItemId),
        includedItemsIds: mapBundleIncludedItemToIds(includedItemsIds),
        price: bundle.price,
        workspaceId: Id.assert(workspace.id),
      })
    }
  }

  @action
  open(bundles: Bundle[]) {
    this.selectedBundles = bundles
    this.setWorkspace(bundles[0].workspace)

    this.show()
  }

  @action.bound
  reset() {
    this.selectedBundles = []
    this.compatibleBundles = []
    this.incompatibleBundleIds.clear()
    this.incompatibleBundleItemIdsMap.clear()
    this.workspace = undefined
  }

  submit = task.resolved(async () => {
    if (!this.workspaceValidation.reset().validate().isValid) {
      return
    }

    const failedPayloads = []

    for (const payload of this.compatibleBundles) {
      try {
        await this.bundleStore.create(payload)
      } catch (e) {
        console.error(e, payload)
        failedPayloads.push(payload)
      }
    }

    if (failedPayloads.length > 0) {
      this.newAlert()
        .withState(AlertState.Error)
        .saying(
          'Error',
          `There was an error while duplicating some bundles(${failedPayloads.length})`,
        )
        .autoDismiss()
        .show()
    }

    // switch to the target workspace and refresh the bundle list
    await this.refreshBundles(this.workspace)

    this.close()
  })

  getIncompatibleItems(bundle: Bundle) {
    return bundle.includedItems.filter((item) =>
      this.incompatibleBundleItemIdsMap
        .get(bundle.Id)
        ?.has(item.inventoryItemId),
    )
  }
}
